import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import * as XLSX from 'xlsx';
import './MaLoge.css';

interface Membre {
  id?: string;
  photo: string;
  loge: string;
  loges?: string[]; // ⭐ Support multi-loges
  nom: string;
  prenom: string;
  adresse: string;
  codePostal: string;
  ville: string;
  mail: string;
  telephone: string;
  grades: string;
  fonction: string;
  statut: string;
}

interface MaLogeProps {
  // Compat: canEdit global (Vénérable/Secrétaire/Admin) calculé dans MaPage
  canEdit: boolean;
  // ⭐ Permissions complètes (pour gérer les droits par loge sélectionnée)
  userPermissions: {
    isAdmin: boolean;
    fonction?: string;
    fonctionsParLoge?: Record<string, string>;
  };
}

function MaLoge({ canEdit, userPermissions }: MaLogeProps) {
  const navigate = useNavigate();
  const [membres, setMembres] = useState<Membre[]>([]);
  const [searchText, setSearchText] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [logeName, setLogeName] = useState('');
  const [logeId, setLogeId] = useState<string | null>(null);
  const [availableLoges, setAvailableLoges] = useState<string[]>([]); // ⭐ Liste des loges de l'utilisateur
  const [allLoges, setAllLoges] = useState<{id: string, nom: string}[]>([]); // ⭐ Toutes les loges

  // ✅ Droits d'édition selon la loge sélectionnée
  const canEditForSelectedLoge = () => {
  if (userPermissions?.isAdmin) return true;

  if (!logeName) return false;

  const roleForLoge = userPermissions?.fonctionsParLoge?.[logeName];

  return roleForLoge === 'Vénérable' || roleForLoge === 'Secrétaire';
};


  // ⭐ Récupérer les loges de l'utilisateur connecté
  useEffect(() => {
    const fetchUserLoges = async () => {
      const auth = getAuth();
      
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            const db = getFirestore();
            
            // Récupérer toutes les loges pour le menu déroulant
            const logesSnapshot = await getDocs(collection(db, 'Loges'));
            const logesData = logesSnapshot.docs.map(doc => ({
              id: doc.id,
              nom: doc.data().nom
            }));
            setAllLoges(logesData);
            
            // Récupérer le membre connecté
            const membresRef = collection(db, 'Membres');
            const q = query(membresRef, where('mail', '==', user.email));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const membreData = querySnapshot.docs[0].data();
              
              // ⭐ Récupérer les loges du membre (multi-loges ou ancien format)
              let userLoges: string[] = [];
              if (membreData.loges && Array.isArray(membreData.loges)) {
                userLoges = membreData.loges;
              } else if (membreData.loge) {
                userLoges = [membreData.loge];
              }
              
              setAvailableLoges(userLoges);
              
              // Sélectionner la première loge par défaut
              if (userLoges.length > 0) {
                setLogeName(userLoges[0]);
              }
            }
          } catch (error) {
            console.error('Erreur lors de la récupération des loges:', error);
          }
        }
      });
    };

    fetchUserLoges();
  }, []);

  // ⭐ Charger les membres quand la loge change
  useEffect(() => {
    if (logeName) {
      fetchMembresForLoge(logeName);
    }
  }, [logeName]);

  const fetchMembresForLoge = async (selectedLoge: string) => {
    const db = getFirestore();
    try {
      // Récupérer l'ID du document de la loge
      const logesSnapshot = await getDocs(collection(db, 'Loges'));
      const logeDoc = logesSnapshot.docs.find(doc => doc.data().nom === selectedLoge);
      
      if (logeDoc) {
        setLogeId(logeDoc.id);
      }

      // ⭐ IMPORTANT : Récupérer TOUS les membres valides puis filtrer en local
      // Car Firebase ne permet pas de combiner array-contains ET where sur le même champ
      const membresQuery = query(
        collection(db, 'Membres'),
        where('statut', '==', 'Valide')
      );
      
      const membresSnapshot = await getDocs(membresQuery);
      const allMembres = membresSnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Gérer la compatibilité
        let logesArray: string[] = [];
        if (data.loges && Array.isArray(data.loges)) {
          logesArray = data.loges;
        } else if (data.loge) {
          logesArray = [data.loge];
        }
        
        return {
          id: doc.id,
          ...data,
          loges: logesArray
        } as Membre;
      });

      // ⭐ FILTRER EN LOCAL pour la loge sélectionnée
      const membresFiltered = allMembres.filter(membre => 
        membre.loges && membre.loges.includes(selectedLoge)
      );

      console.log(`✅ ${membresFiltered.length} membres trouvés pour "${selectedLoge}"`);
      console.log('Membres:', membresFiltered.map(m => `${m.nom} ${m.prenom}`));
      setMembres(membresFiltered);
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      setMembres([]);
    }
  };

  // ⭐ Gestion du changement de loge
  const handleLogeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLoge = e.target.value;
    setLogeName(newLoge);
  };

  const handleSort = (column: string) => {
    if (column === 'nom' || column === 'grades' || column === 'fonction') {
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleModifierLoge = () => {
    if (logeId) {
      navigate(`/mapage/modifier-loge/${logeId}`);
    } else {
      console.error('ID de loge non disponible');
    }
  };

  const exportToExcel = () => {
    const dataToExport = sortedMembres.map(membre => ({
      Photo: membre.photo === 'Aucune photo' ? 'Non' : 'Oui',
      Nom: membre.nom,
      Prénom: membre.prenom,
      Grade: membre.grades,
      Fonction: membre.fonction,
      'Loges': membre.loges && membre.loges.length > 0 
        ? membre.loges.join(', ')
        : membre.loge,
      Adresse: membre.adresse,
      'Code Postal': membre.codePostal,
      Ville: membre.ville,
      Email: membre.mail,
      Téléphone: membre.telephone
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Membres Valides");
    XLSX.writeFile(wb, `membres_valides_${logeName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.xlsx`);
  };

  const filteredMembres = membres.filter((membre) => {
    if (!searchText) return true;
    const searchRegex = new RegExp(searchText, 'i');
    return searchRegex.test(membre.nom) || 
           searchRegex.test(membre.prenom) || 
           searchRegex.test(membre.mail) ||
           searchRegex.test(membre.grades) ||
           searchRegex.test(membre.fonction);
  });

  const sortedMembres = [...filteredMembres].sort((a, b) => {
    if (sortColumn === 'nom') {
      return sortDirection === 'asc'
        ? a.nom.localeCompare(b.nom)
        : b.nom.localeCompare(a.nom);
    } else if (sortColumn === 'grades') {
      return sortDirection === 'asc'
        ? (a.grades || '').localeCompare(b.grades || '')
        : (b.grades || '').localeCompare(a.grades || '');
    } else if (sortColumn === 'fonction') {
      return sortDirection === 'asc'
        ? (a.fonction || '').localeCompare(b.fonction || '')
        : (b.fonction || '').localeCompare(a.fonction || '');
    }
    return 0;
  });

  return (
    <div className="ma-loge-container">
      <h1>Membres de Loge</h1>
      
      <div className="button-container">
        <div className="buttons-group">
          {/* ⭐ MENU DÉROULANT POUR SÉLECTIONNER LA LOGE */}
          <div className="loge-selector">
            <label htmlFor="loge-select">Loge :</label>
            <select 
              id="loge-select"
              className="loge-select"
              value={logeName}
              onChange={handleLogeChange}
            >
              <option value="">-- Sélectionner une loge --</option>
              {availableLoges.map((loge) => (
                <option key={loge} value={loge}>
                  {loge}
                </option>
              ))}
            </select>
          </div>
          
          <button
            className="extraire-btn"
            onClick={exportToExcel}
            disabled={!logeName}
          >
            Extraire
          </button>
          
          {/* Afficher les boutons uniquement pour les utilisateurs autorisés */}
          {canEditForSelectedLoge() && (
            <>
              <button
                className="ajouter-btn"
                onClick={() =>
                  navigate(`/mapage/ajouter-membre-loge?loge=${encodeURIComponent(logeName)}`)
                }

                disabled={!logeName}
              >
                Ajouter Membres Loge
              </button>
              <button
                className="modifier-btn"
                onClick={handleModifierLoge}
                disabled={!logeId}
              >
                Modifier la Loge
              </button>
            </>
          )}
        </div>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher par nom, prénom, grade ou fonction"
            value={searchText}
            onChange={handleSearch}
          />
        </div>
      </div>

      {/* ⭐ Message si aucune loge sélectionnée */}
      {!logeName && (
        <div className="no-loge-selected">
          <p>Veuillez sélectionner une loge pour afficher les membres.</p>
        </div>
      )}

      {/* ⭐ Affichage du nombre de membres */}
      {logeName && (
        <p className="membres-count">
          {sortedMembres.length} membre{sortedMembres.length > 1 ? 's' : ''} valide{sortedMembres.length > 1 ? 's' : ''} 
          {logeName && ` - ${logeName}`}
        </p>
      )}

      {/* ⭐ Tableau des membres */}
      {logeName && (
         <div className="table-responsive">
            <table className="membres-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th
                    onClick={() => handleSort('nom')}
                    className="sortable-column"
                  >
                    Nom {getSortIcon('nom')}
                  </th>
                  <th>Prénom</th>
                  <th
                    onClick={() => handleSort('grades')}
                    className="sortable-column"
                  >
                    Grade {getSortIcon('grades')}
                  </th>
                  <th
                    onClick={() => handleSort('fonction')}
                    className="sortable-column"
                  >
                    Fonction {getSortIcon('fonction')}
                  </th>
                  <th>Adresse</th>
                  <th>Code Postal</th>
                  <th>Ville</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  {canEditForSelectedLoge() && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sortedMembres.length === 0 ? (
                  <tr>
                    <td colSpan={canEditForSelectedLoge() ? 11 : 10} style={{ textAlign: 'center', padding: '20px' }}>
                      Aucun membre valide trouvé pour cette loge.
                    </td>
                  </tr>
                ) : (
                  sortedMembres.map((membre) => (
                    <tr key={membre.id}>
                      <td>
                        {membre.photo !== 'Aucune photo' ? (
                          <img src={membre.photo} alt={`${membre.nom} ${membre.prenom}`} className="membre-photo" />
                        ) : (
                          'Aucune photo'
                        )}
                      </td>
                      <td>{membre.nom}</td>
                      <td>{membre.prenom}</td>
                      <td>{membre.grades}</td>
                      <td>{membre.fonctionsParLoge?.[logeName] || ''}</td>
                      <td>{membre.adresse}</td>
                      <td>{membre.codePostal}</td>
                      <td>{membre.ville}</td>
                      <td>{membre.mail}</td>
                      <td>{membre.telephone}</td>
                      {canEditForSelectedLoge() && (
                        <td className="actions-cell">
                          <button
                            className="modifier-membre-btn"
                            onClick={() => {
                              if (membre.id) {
                                navigate(`/mapage/modifier-membre/${membre.id}?loge=${encodeURIComponent(logeName)}`);

                              }
                            }}
                            disabled={!membre.id}
                            title={!membre.id ? 'Impossible: ID manquant' : 'Modifier ce membre'}
                          >
                            Modifier
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          )}
    </div>
  );
}

export default MaLoge;